/**
 * Phone numbers as a country and a national number, rendered the way the phone
 * itself would render them.
 *
 * Stored as E.164 (`+16015721536`) because that is the only form that survives
 * being dialled from another country, and shown as `(601) 572-1536` because that
 * is what the number looks like to the person who owns it. The two are never the
 * same string, so the split lives here rather than in a component.
 */
export interface Country {
  code: string
  dial: string
  flag: string
  /** Digits in the national number, used to know when it is complete. */
  length: number
}

/* The countries these practices are actually in, plus the neighbours a Swiss or
   US practice is most likely to add. Deliberately short: a picker of 200 entries
   is worse at this job than one that fits on the screen. */
export const COUNTRIES: Country[] = [
  { code: 'US', dial: '+1', flag: '🇺🇸', length: 10 },
  { code: 'CH', dial: '+41', flag: '🇨🇭', length: 9 },
  { code: 'IL', dial: '+972', flag: '🇮🇱', length: 9 },
  { code: 'DE', dial: '+49', flag: '🇩🇪', length: 11 },
  { code: 'FR', dial: '+33', flag: '🇫🇷', length: 9 },
  { code: 'GB', dial: '+44', flag: '🇬🇧', length: 10 },
  { code: 'CA', dial: '+1', flag: '🇨🇦', length: 10 },
]

export const countryFor = (code: string | null | undefined): Country =>
  COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0]!

/** Maps a practice's free-text country onto a dialling country. */
export function countryFromName(name: string | null): Country {
  const n = (name ?? '').trim().toLowerCase()
  if (n.startsWith('switz') || n === 'ch' || n === 'suisse') return countryFor('CH')
  if (n.startsWith('isr') || n === 'il') return countryFor('IL')
  if (n.startsWith('germ') || n === 'de') return countryFor('DE')
  if (n.startsWith('fran') || n === 'fr') return countryFor('FR')
  if (n.startsWith('united king') || n === 'uk' || n === 'gb') return countryFor('GB')
  if (n.startsWith('can') || n === 'ca') return countryFor('CA')
  return countryFor('US')
}

/**
 * Splits a stored number into its country and national digits.
 *
 * A number with no `+` predates this field having a country at all — Oryx stored
 * whatever was typed — so the practice's own country is the best guess, and a
 * leading trunk `0` (`0765588560`) is dropped because it is not dialled from
 * outside.
 */
export function parsePhone(stored: string | null, fallback: Country): { country: Country; national: string } {
  const raw = (stored ?? '').trim()
  if (!raw) return { country: fallback, national: '' }

  if (raw.startsWith('+') || raw.startsWith('00')) {
    const digits = raw.replace(/^00/, '').replace(/\D/g, '')
    /* Longest dial code first, so +41 is not read as +4. */
    const byLength = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
    for (const c of byLength) {
      const dial = c.dial.slice(1)
      if (digits.startsWith(dial)) return { country: c, national: digits.slice(dial.length) }
    }
    return { country: fallback, national: digits }
  }

  const digits = raw.replace(/\D/g, '').replace(/^0+/, '')
  return { country: fallback, national: digits }
}

/** `(601) 572-1536` for +1, and the local grouping elsewhere. */
export function formatNational(country: Country, national: string): string {
  const d = national.replace(/\D/g, '')
  if (!d) return ''
  if (country.dial === '+1') {
    if (d.length <= 3) return `(${d}`
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`
  }
  if (country.code === 'CH') {
    /* 76 558 85 60 — two, three, two, two. */
    return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 7), d.slice(7, 9)].filter(Boolean).join(' ')
  }
  if (country.code === 'IL') {
    return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 9)].filter(Boolean).join('-')
  }
  return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim()
}

/** What gets stored: `+16015721536`, or null when nothing was entered. */
export function toE164(country: Country, national: string): string | null {
  const d = national.replace(/\D/g, '')
  return d ? `${country.dial}${d}` : null
}

/** Read-only rendering of a stored number, for tables and detail rows. */
export function displayPhone(stored: string | null, fallbackCountry?: string | null): string {
  if (!stored) return ''
  const { country, national } = parsePhone(stored, countryFromName(fallbackCountry ?? null))
  const formatted = formatNational(country, national)
  return formatted ? `${country.flag} ${country.dial} ${formatted}` : stored
}

/**
 * The diallable form of a stored number: `+16015721536`.
 *
 * Numbers that arrived before this field had a country are stored bare
 * (`6015721536` on East Cobb), so the country is recovered the same way the
 * editor recovers it — from the number itself where it says, and from the
 * practice otherwise. This is what gets copied, never the bracketed display
 * form, which no dialler accepts.
 */
export function e164(stored: string | null, fallbackCountry?: string | null): string | null {
  if (!stored) return null
  const { country, national } = parsePhone(stored, countryFromName(fallbackCountry ?? null))
  return toE164(country, national)
}
